import { __ } from '@wordpress/i18n';
import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, RichText, InnerBlocks, InspectorControls } from '@wordpress/block-editor';
import { 
    PanelBody, 
    TabPanel, 
    Button,
    ToggleControl
} from '@wordpress/components';

// Import custom components
import Typography from '../../components/Typography';
import VisibilityControl from '../../components/VisibilityControl';
import Spacing from '../../components/Spacing';
import Border from '../../components/Border';
import FlatColorPicker from '../../components/FlatColorPicker';
import { getSpacingStyles, getBorderStyles, getTypographyStyles } from '../../utils/styles';
import { Placeholder } from '@wordpress/components';

registerBlockType('kanews/faq', {
    apiVersion: 2,
    title: __('SSS', 'kanews'),
    category: 'kanews',
    icon: 'editor-help',
    supports: {
        html: false,
        align: ['wide', 'full'],
    },
    attributes: {
        title: {
            type: 'string',
            default: __('Sık Sorulan Sorular', 'kanews')
        },
        showTitle: {
            type: 'boolean',
            default: true
        },
        enableSchema: {
            type: 'boolean',
            default: true
        },
        typography: {
            type: 'object',
            default: {}
        },
        background: {
            type: 'string',
            default: ''
        },
        titleSpacing: {
            type: 'number',
            default: ''
        },
        margin: {
            type: 'object',
            default: {}
        },
        padding: {
            type: 'object',
            default: {}
        },
        border: {
            type: 'object',
            default: {}
        },
        visibility: {
            type: 'object',
            default: {
                desktop: false,
                tablet: false,
                mobile: false
            }
        },
        accordionId: {
            type: 'string',
            default: ''
        }
    },
    edit: ({ attributes, setAttributes }) => {
        const {
            title,
            showTitle,
            enableSchema,
            typography,
            background,
            titleSpacing,
            margin,
            padding,
            border,
            visibility,
            accordionId
        } = attributes;

        const visibilityClasses = [
            visibility?.desktop ? 'hidden-desktop' : '',
            visibility?.tablet ? 'hidden-tablet' : '',
            visibility?.mobile ? 'hidden-mobile' : ''
        ].filter(Boolean);

        const blockProps = useBlockProps();

        const wrapperProps = {
            className: ['kanews-faq-wrapper', 'kanews-block-margin', 'kanews-block-border', 'kanews-block-padding', ...visibilityClasses].filter(Boolean).join(' '),
            style: {
                ...getSpacingStyles(margin, 'margin'),
                ...getSpacingStyles(padding, 'padding'),
                ...getBorderStyles(border, 'border'),
                backgroundColor: background
            }
        };

        // Use existing accordionId or generate a new one
        const currentAccordionId = accordionId || `kanews-faq-accordion-${blockProps.id}`;
        if (!accordionId) {
            setAttributes({ accordionId: currentAccordionId });
        }

        const tabs = [
            {
                name: 'content',
                title: __('İçerik', 'kanews'),
                className: 'content-tab'
            },
            {
                name: 'design',
                title: __('Tasarım', 'kanews'),
                className: 'design-tab'
            },
            {
                name: 'visibility',
                title: __('Görünürlük', 'kanews'),
                className: 'visibility-tab'
            }
        ];

        return (
            <>
                <InspectorControls>
                    <TabPanel
                        className="kanews-tab-panel"
                        tabs={tabs}
                    >
                        {(tab) => {
                            return (
                                <>
                                    {tab.name === 'content' && (
                                        <PanelBody>
                                            <RichText
                                                tagName="h2"
                                                value={title}
                                                onChange={(value) => setAttributes({ title: value })}
                                                placeholder={__('SSS Başlığı', 'kanews')}
                                                className="kanews-faq-title"
                                            />
                                            <ToggleControl
                                                label={__('Başlığı Göster', 'kanews')}
                                                checked={showTitle}
                                                onChange={(value) => setAttributes({ showTitle: value })}
                                                help={__('SSS bloğunun başlığını gösterir veya gizler.', 'kanews')}
                                            />
                                            <ToggleControl
                                                label={__('Schema Desteği', 'kanews')}
                                                checked={enableSchema}
                                                onChange={(value) => setAttributes({ enableSchema: value })}
                                                help={__('SSS bloğunu arama motorları için yapılandırılmış veri olarak işaretler.', 'kanews')}
                                            />
                                        </PanelBody>
                                    )}
                                    {tab.name === 'design' && (
                                        <>
                                            <PanelBody title={__('Tipografi', 'kanews')}>
                                                <Typography
                                                    label={__('Başlık Tipografisi', 'kanews')}
                                                    value={typography}
                                                    onChange={(value) => setAttributes({ typography: value })}
                                                />
                                                <Spacing
                                                    label={__('Başlık Boşluğu', 'kanews')}
                                                    value={titleSpacing}
                                                    onChange={(value) => setAttributes({ titleSpacing: value })}
                                                />
                                            </PanelBody>
                                            <PanelBody title={__('Arkaplan', 'kanews')} initialOpen={false}>
                                                <FlatColorPicker
                                                    label={__('Arkaplan Rengi', 'kanews')}
                                                    value={background}
                                                    onChange={(value) => setAttributes({ background: value })}
                                                />
                                            </PanelBody>
                                            <PanelBody title={__('Kenarlık', 'kanews')} initialOpen={false}>
                                                <Border
                                                    label={__('Kenarlık', 'kanews')}
                                                    value={border}
                                                    onChange={(value) => setAttributes({ border: value })}
                                                />
                                            </PanelBody>
                                            <PanelBody title={__('Boşluklar', 'kanews')} initialOpen={false}>
                                                <Spacing
                                                    label={__('Dış Boşluk', 'kanews')}
                                                    value={margin}
                                                    onChange={(value) => setAttributes({ margin: value })}
                                                />
                                                <Spacing
                                                    label={__('İç Boşluk', 'kanews')}
                                                    value={padding}
                                                    onChange={(value) => setAttributes({ padding: value })}
                                                />
                                            </PanelBody>
                                        </>
                                    )}
                                    {tab.name === 'visibility' && (
                                        <PanelBody>
                                            <VisibilityControl
                                                value={visibility}
                                                onChange={(value) => setAttributes({ visibility: value })}
                                            />
                                        </PanelBody>
                                    )}
                                </>
                            );
                        }}
                    </TabPanel>
                </InspectorControls>

                <div {...blockProps}>
                    <div {...wrapperProps}>
                        {showTitle && (
                            <RichText
                                tagName="h2"
                                value={title}
                                onChange={(value) => setAttributes({ title: value })}
                                placeholder={__('SSS Başlığı', 'kanews')}
                                className="kanews-faq-title kanews-block-margin"
                                style={{...getTypographyStyles(typography), ...getSpacingStyles(titleSpacing, 'margin')}}
                            />
                        )}
                        <div className="collapse" id={currentAccordionId} role="tablist" aria-multiselectable="true">
                            <InnerBlocks
                                allowedBlocks={['kanews/faq-item']}
                                template={[
                                    ['kanews/faq-item', { accordionId: currentAccordionId }],
                                    ['kanews/faq-item', { accordionId: currentAccordionId }],
                                    ['kanews/faq-item', { accordionId: currentAccordionId }]
                                ]}
                                templateLock={false}
                            />
                        </div>
                        {enableSchema && (
                            <script type="application/ld+json">
                                {JSON.stringify({
                                    '@context': 'https://schema.org',
                                    '@type': 'FAQPage',
                                    'mainEntity': [] // This will be populated by the server-side render
                                })}
                            </script>
                        )}
                    </div>
                </div>
            </>
        );
    },
    save: ({ attributes }) => {
        const {
            title,
            showTitle,
            enableSchema,
            typography,
            background,
            titleSpacing,
            margin,
            padding,
            border,
            visibility,
            accordionId
        } = attributes;

        const visibilityClasses = [
            visibility?.desktop ? 'hidden-desktop' : '',
            visibility?.tablet ? 'hidden-tablet' : '',
            visibility?.mobile ? 'hidden-mobile' : ''
        ].filter(Boolean);

        const wrapperProps = {
            className: ['kanews-faq-wrapper', 'kanews-block-margin', 'kanews-block-border', 'kanews-block-padding', ...visibilityClasses].filter(Boolean).join(' '),
            style: {
                ...getSpacingStyles(margin, 'margin'),
                ...getSpacingStyles(padding, 'padding'),
                ...getBorderStyles(border, 'border'),
                backgroundColor: background
            }
        };

        return (
            <div {...wrapperProps}>
                {showTitle && (
                    <RichText.Content
                        tagName="h2"
                        value={title}
                        className="kanews-faq-title kanews-block-margin"
                        style={{...getTypographyStyles(typography), ...getSpacingStyles(titleSpacing, 'margin')}}
                    />
                )}
                <div className="collapse" id={accordionId} role="tablist" aria-multiselectable="true">
                    <InnerBlocks.Content />
                </div>
                {enableSchema && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'FAQPage',
                            'mainEntity': [] // This will be populated by the server-side render
                        })}
                    </script>
                )}
            </div>
        );
    }
}); 